import { useState, type ReactNode } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/Tabs';

import { FieldInput } from './FieldInput';
import { isVisible, type FormField } from './formModel';
import styles from './FormFields.module.scss';

export interface FormFieldsProps {
  fields: readonly FormField[];
  values: Record<string, unknown>;
  /** Ошибки сервера по полному пути: `title`, `body.2.text`, `faq.0.q`. */
  errors: Record<string, string>;
  /** Путь к этому уровню: пусто у записи, `body.2.` у блока, `faq.0.` у элемента группы. */
  path?: string;
  siteID: string;
  onChange: (key: string, value: unknown) => void;
  /** Обёртка вокруг поля — для меток «это поле правит Анна». */
  decorate?: (field: FormField, node: ReactNode) => ReactNode;
}

const MAIN_TAB = '';

/**
 * Поля одного уровня с раскладкой: вкладки, два поля в строку, условие
 * показа. Одна и та же для записи, блока и элемента группы — правила
 * раскладки не должны расходиться между ними.
 */
export function FormFields({
  fields,
  values,
  errors,
  path = '',
  siteID,
  onChange,
  decorate,
}: FormFieldsProps) {
  const shown = fields.filter((field) => isVisible(field, values));

  const tabs: string[] = [];

  for (const field of shown) {
    if (!tabs.includes(field.tab)) {
      tabs.push(field.tab);
    }
  }

  // Основная вкладка — всегда первая, даже если первое поле лежит на другой.
  tabs.sort((a, b) => (a === MAIN_TAB ? -1 : b === MAIN_TAB ? 1 : 0));

  const [active, setActive] = useState(tabs[0] ?? MAIN_TAB);
  const current = tabs.includes(active) ? active : (tabs[0] ?? MAIN_TAB);

  const grid = (list: FormField[]) => (
    <div className={styles['grid']}>
      {list.map((field) => {
        const node = (
          <FieldInput
            field={field}
            value={values[field.key]}
            errorKey={path + field.key}
            errors={errors}
            siteID={siteID}
            onChange={(next) => {
              onChange(field.key, next);
            }}
          />
        );

        return (
          <div
            key={field.id}
            className={field.width === 'half' ? styles['cell--half'] : styles['cell--full']}
          >
            {decorate === undefined ? node : decorate(field, node)}
          </div>
        );
      })}
    </div>
  );

  if (tabs.length <= 1) {
    return grid(shown);
  }

  // Ошибка на другой вкладке не должна теряться: на вкладке — их число.
  const errorsOn = (tab: string) =>
    shown
      .filter((field) => field.tab === tab)
      .filter((field) =>
        Object.keys(errors).some(
          (key) => key === path + field.key || key.startsWith(`${path}${field.key}.`),
        ),
      ).length;

  return (
    <Tabs value={current} onValueChange={setActive}>
      <TabsList>
        {tabs.map((tab) => {
          const count = errorsOn(tab);

          return (
            <TabsTrigger
              key={tab}
              value={tab}
              active={tab === current}
              {...(count > 0 ? { count } : {})}
            >
              {tab === MAIN_TAB ? 'Основное' : tab}
            </TabsTrigger>
          );
        })}
      </TabsList>

      {tabs.map((tab) => (
        <TabsContent key={tab} value={tab} className={styles['tab']}>
          {grid(shown.filter((field) => field.tab === tab))}
        </TabsContent>
      ))}
    </Tabs>
  );
}
